import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface CompanyInput {
  organization_id: string;
  name: string;
  domain?: string;
  website?: string;
  phone?: string;
  industry?: string;
  size?: string;
  annual_revenue?: number;
  arr_potential?: number;
  employee_count?: number;
  territory?: string;
  lifecycle_stage?: string;
  account_tier?: 'A' | 'B' | 'C';
  owner_user_id?: string;
  buying_committee?: Array<{ role: string; contact_id: string }>;
  last_activity_at?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
}

export interface Company extends CompanyInput {
  id: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
  enrichment_confidence?: number;
  enrichment_sources?: string[];
  enriched_at?: string;
  account_intent_score?: number;
}

export interface ListCompaniesParams {
  organization_id: string;
  search?: string;
  limit?: number;
  offset?: number;
  account_tier?: 'A' | 'B' | 'C';
  lifecycle_stage?: string;
  territory?: string;
  owner_user_id?: string;
  min_arr?: number;
  max_arr?: number;
}

const companiesService = {
  async list(params: ListCompaniesParams): Promise<{ data: Company[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'companiesService.list'
    });
    const url = buildApiUrl('/api/crm/companies', params as any);
    return fetchJSON(url, {
      description: 'List companies',
      context: { organization_id: params.organization_id, search: params.search, limit: params.limit, offset: params.offset }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: Company | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'companiesService.get'
    });
    const url = buildApiUrl(`/api/crm/companies/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get company',
      context: { id, organization_id }
    });
  },
  async create(payload: CompanyInput): Promise<{ data: Company }>{
    validateRequiredParams(payload, ['organization_id', 'name'], {
      service: 'companiesService.create'
    });
    const url = buildApiUrl('/api/crm/companies');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create company',
      context: { organization_id: payload.organization_id }
    });
  },
  async update(id: string, payload: Partial<CompanyInput> & { organization_id: string }): Promise<{ data: Company }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'companiesService.update'
    });
    const url = buildApiUrl(`/api/crm/companies/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update company',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'companiesService.remove'
    });
    const url = buildApiUrl(`/api/crm/companies/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete company',
      context: { id, organization_id }
    });
  }
};

export default companiesService;
